import React, { useEffect, useRef, useState } from "react"
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import CloseIcon from "../../images/CloseIcon";
import {
  deleteProductModifier,
  getListProductModifiers,
  ProductModifierListItem,
} from '../../services/productModifiers.service';
import { showDeleteToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import PlusIconWhite from "../../images/PlusIconWhite";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import { useResponsive } from "../../hook/useResponsive";
import OptionIcon from "../../images/OptionIcon";
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  TEXT_DIM,
  cells,
  t,
} from "../../components/list";

const MODIFIER_TYPE_LABELS: Record<number, string> = {
  1: 'Extras',
  2: 'Services',
  3: 'Restrictions',
};

const typeLabelOf = (m: ProductModifierListItem, language: any) => {
  const label = MODIFIER_TYPE_LABELS[m.modifier_type];
  return label ? t(language, label, label) : String(m.modifier_type);
};

const ProductModifiers = () => {
  const preLanguage = localStorage.getItem("language");
  const { isMobile } = useResponsive();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const [language, setLanguage] = useState<any>(null);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>("");
  const inforTable: any = {
    img: <OptionIcon />,
    title: language?.["No modifiers yet"] ?? "No modifiers yet",
    path: "/productModifiers/new",
    desc: language?.["Manage product modifiers here. Add a modifier to prompt on the POS devices."] ?? "Manage product modifiers here. Add a modifier to prompt on the POS devices.",
    titleCreate: language?.["Add modifier"] ?? "Add modifier"
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /**
   * The endpoint returns every modifier (no paging, no count), so — as
   * before — search and pagination happen in memory. The single search box
   * matches what the old per-column inputs matched: name, type label, min /
   * max answers and priority.
   */
  const fetchModifiers = async (query: ListQuery) => {
    webLoadingFalse();
    const response = await getListProductModifiers({});
    let modifiers: ProductModifierListItem[] = response?.data?.modifiers ?? [];
    const needle = query.search.trim().toLowerCase();
    if (needle) {
      modifiers = modifiers.filter(m =>
        [
          m.name,
          // The cell shows the translated type; match that as well as the English label.
          MODIFIER_TYPE_LABELS[m.modifier_type] ?? String(m.modifier_type),
          typeLabelOf(m, language),
          String(m.min_answer),
          String(m.max_answer),
          String(m.display_order),
        ].some(v => String(v ?? '').toLowerCase().includes(needle))
      );
    }
    const start = (query.page - 1) * query.limit;
    return { rows: modifiers.slice(start, start + query.limit), count: modifiers.length };
  };

  const goToModifier = (id: string) => webNavigate('/' + preLanguage + '/productModifiers/' + id);

  const handleShowDelete = (id: string) => {
    setShowDelete(true);
    setIdDelete(id);
  };

  const handleDeleteModifier = async () => {
    setShowDelete(false);
    try {
      await deleteProductModifier(idDelete);
      showDeleteToast(language?.['Product Modifier Deleted !'] ?? 'Product Modifier Deleted !');
    } catch (error: any) {
      console.error("Error deleting modifier:", error);
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<ProductModifierListItem>[] = [
    {
      key: 'index',
      label: '#',
      width: 48,
      hideable: false,
      // Running number across pages, as the old table showed it.
      render: (_m, ctx) => (
        <div className="font-inter" style={{ fontSize: '13px', color: TEXT_DIM }}>
          #{ctx.index + 1 + ctx.limit * (ctx.page - 1)}
        </div>
      ),
      mobile: 'hidden',
    },
    {
      key: 'name',
      label: 'Name',
      width: 260,
      hideable: false,
      render: cells.primary(m => m.name),
      mobile: 'title',
      onClick: m => goToModifier(String(m.id)),
    },
    {
      key: 'type',
      label: 'Type',
      width: 120,
      value: (m, ctx) => typeLabelOf(m, ctx.language),
      render: cells.typePill((m, ctx) => typeLabelOf(m, ctx.language)),
      mobile: 'subtitle',
    },
    { key: 'min_answer', label: 'Min. Answer', width: 100, align: 'right', mobile: 'sheet' },
    { key: 'max_answer', label: 'Max. Answer', width: 100, align: 'right', mobile: 'sheet' },
    { key: 'display_order', label: 'Priority', width: 80, align: 'right', mobile: 'sheet' },
    {
      key: 'last_updated',
      label: 'Last Updated',
      width: 130,
      render: cells.date(m => m.last_updated),
      mobile: 'sheet',
    },
  ];

  const rowActions: ListRowAction<ProductModifierListItem>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIconV2 />,
      sheetIcon: 'inventory',
      onRun: m => goToModifier(String(m.id)),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteIconV2 />,
      sheetIcon: 'close',
      danger: true,
      onRun: m => handleShowDelete(String(m.id)),
    },
  ];

  return (
    <>
      {/* Desktop is a flex column so the list fills the height left after the title row. */}
      <div
        className="h-full relative"
        style={{
          overflowY: isMobile ? "auto" : undefined,
          paddingBottom: isMobile ? "24px" : undefined,
          display: isMobile ? undefined : 'flex',
          flexDirection: isMobile ? undefined : 'column',
        }}
      >
        <div className="">
          <div className="lg:flex items-center justify-between">
            <p
              style={{ color: '#222525' }}
              className="text-[24px] leading-[130%] font-medium font-inter"
            >
              {language?.['Product modifiers'] ?? 'Product modifiers'}
            </p>
            <div
              className="cursor-pointer flex items-center justify-center"
              style={{
                gap: '8px',
                padding: "8px 24px 8px 16px",
                backgroundColor: '#0F7679',
                borderRadius: '30px',
                width: "fit-content",
                marginTop: isMobile ? "20px" : '0px',
              }}
              onClick={() => {
                webNavigate('/' + preLanguage + '/productModifiers/new');
              }}
            >
              <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
                <PlusIconWhite />
              </div>
              <p
                style={{ color: '#FFFFFF' }}
                className="text-[14px] leading-[130%] font-medium font-inter"
              >
                {language?.['Add modifier'] ?? 'Add modifier'}
              </p>
            </div>
          </div>
        </div>
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchModifiers}
          searchPlaceholder="Search modifiers"
          searchInputRef={searchInputRef}
          rowActions={rowActions}
          countNoun="modifiers"
          emptyState={inforTable}
        />
      </div>
      <div
        className={`fixed w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
        style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100vw", height: "100vh", left: 0, top: 0 }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`}
          style={{
            padding: "24px", borderRadius: "24px", backgroundColor: "#FFFFFF", width: "530px", maxWidth: "calc(100vw - 32px)", transition: "transform 0.3s ease, opacity 0.3s ease",
          }}
        >
          <div className="flex items-center justify-between">
            <p
              style={{ fontSize: '20px', color: '#222525' }}
              className="font-semibold font-inter leading-[24px]"
            >
              {language?.['Delete modifier?'] ?? 'Delete modifier?'}
            </p>
            <div
              onClick={() => setShowDelete(false)}
              className="flex items-center justify-center cursor-pointer"
              style={{ width: '24px', height: '24px', borderRadius: '100%' }}
            >
              <CloseIcon />
            </div>
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
            <p className="text-[14px] font-inter leading-[24px]" style={{ color: '#6A7878' }}>
              {language?.['This modifier will be permanently deleted. Are you sure you want to continue?'] ?? 'This modifier will be permanently deleted. Are you sure you want to continue?'}
            </p>
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <div
              onClick={() => setShowDelete(false)}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
              }}
            >
              {language?.['Cancel'] ?? 'Cancel'}
            </div>
            <div
              onClick={handleDeleteModifier}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                backgroundColor: '#BA0E13',
                color: '#FFFFFF',
              }}
            >
              {language?.['Delete'] ?? 'Delete'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductModifiers;
